import {
  memo,
  FunctionComponent,
  useState,
  useCallback,
  SyntheticEvent,
} from 'react';
import { Item as ItemType, ItemsOdd as ItemsOddType } from '~types';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { useOdd } from '~components/hooks';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Loader } from '~components/Loader';
import Currency from '~components/Currency';
import { Price } from '~ui/index';
import { useGetCase } from '~components/home/hooks';
import { useConfig } from '~components/hooks';
import NotFound from '../error/NotFound';
import { useToggle } from 'react-use';
import { copyToClipboard } from '~frontend/utils/clickboard';

import '../../scss/pages/_fair-play.scss';

interface ItemProps {
  getItem: ItemType;
  itemsOdds: ItemsOddType[];
}

const Item: FunctionComponent<ItemProps> = memo(({ getItem, itemsOdds }) => {
  const findItem = _.find(itemsOdds, ['id', getItem.id]);
  const getConfig = useConfig();

  return (
    <div className="fair-play-case-table-body-row">
      <div className="fair-play-case-table-body-td">
        <div className="top-drop-item" data-rar={getItem.color}>
          <div className="honey"></div>
          <div
            className="drop-img"
            style={{
              backgroundImage: `url(${getItem.getImage}/200x200)`,
            }}
          ></div>
          <div className="in-case-info">
            <div className="in-case-item-name">{getItem.market_hash_name}</div>
          </div>
        </div>
        <div className="case-name">{getItem.market_hash_name}</div>
      </div>
      <div className="fair-play-case-table-body-td">
        <div className="in-case-item status-4">
          <div className="in-case-cost">
            {getConfig.isEU ? (
              <>
                <Currency />
                <Price sum={findItem?.price} />
              </>
            ) : (
              <>
                <Price sum={findItem?.price} />
                <Currency />
              </>
            )}
          </div>
        </div>
      </div>
      <div className="fair-play-case-table-body-td">
        <span className="range">
          {findItem.lowerRange} - {findItem.upperRange}
        </span>
      </div>
      <div className="fair-play-case-table-body-td">
        <div className="in-case-item status-4">
          <div className="in-case-cost">
            {getConfig.isEU ? (
              <>
                <Currency />
                <Price sum={findItem?.price} />
              </>
            ) : (
              <>
                <Price sum={findItem?.price} />
                <Currency />
              </>
            )}
          </div>
        </div>
        <div className="range">
          {findItem.lowerRange} - {findItem.upperRange}
        </div>
        <div className="drop-chance">{findItem.chance}%</div>
      </div>
    </div>
  );
});

interface Props {
  defaultHashId: string;
}

const Odds: FunctionComponent<Props> = ({ defaultHashId }) => {
  const getConfig = useConfig();
  const [hashId, setHashId] = useState(defaultHashId);
  const [value, setValue] = useState('');
  const [isCopy, toggleCopy] = useToggle(false);
  const { t } = useTranslation();

  const { loading, error, data, fetchMore } = useOdd(hashId);

  const getOdd = data?.getOdd;

  const onChange = useCallback(
    (event: any) => {
      setValue(event.currentTarget.value);
    },
    [getOdd],
  );

  const onClick = () => {
    copyToClipboard(`${getOdd.hashId}`);
    toggleCopy();

    setTimeout(() => {
      toggleCopy(false);
    }, 1000);
  };

  const onSearch = useCallback(
    async (event: SyntheticEvent) => {
      try {
        event.preventDefault();

        const { data } = await fetchMore({ variables: { hashId: value } });

        if (!data?.getOdd) {
          throw new Error('wrong_period');
        }

        setHashId(value);
      } catch (error) {
        toast.error(
          <>
            <div className="notify-title">{t('Error')}!</div>
            <div className="notify-text">{t(error.message)}</div>
          </>,
        );
      }
    },
    [value],
  );

  if (loading) {
    return <Loader />;
  } else if (error) {
    return <>Odds | Error! {error.message}</>;
  }

  if (!getOdd) {
    return <NotFound />;
  }

  return (
    <>
      <Helmet>
        <title>
          {t(`Odds_Title`, {
            hostname: getConfig?.hostname.toUpperCase(),
            caseName: getOdd?.getCase?.getName,
          })}
        </title>
        <meta
          name="description"
          content={t(`Odds_Desc`, {
            hostname: getConfig?.hostname.toUpperCase(),
            caseName: getOdd?.getCase?.getName,
          })}
        />
      </Helmet>
      <div className="page-fair-play">
        <div className="page-fair-play-content">
          <div
            className="fair-play-case fair-play-case-top"
            data-rar={getOdd.getCase?.color}
          >
            <div className="fair-play-case-roll-history">
              <h1>
                <div className="title">{t('History of odds for')}:</div>
                <div
                  className="case-play-name"
                  data-rar={getOdd?.getCase?.color}
                >
                  {getOdd?.getCase?.getName}
                </div>
              </h1>
              {getOdd?.getCase?.priceOld > 0 && (
                // SCRUM-151 hide old price and hide bonuses
                <></>
                // <div className="open-case old-sale">
                //   {getConfig.isEU ? (
                //     <>
                //       <Currency />
                //       <Price sum={getOdd?.getCase?.priceOld} />
                //     </>
                //   ) : (
                //     <>
                //       <Price sum={getOdd?.getCase?.priceOld} />
                //       <Currency />
                //     </>
                //   )}
                // </div>
              )}
              <div className="case-cost" data-rar={getOdd?.getCase?.color}>
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={getOdd?.getCase?.price} />
                  </>
                ) : (
                  <>
                    <Price sum={getOdd?.getCase?.price} />
                    <Currency />
                  </>
                )}
              </div>
            </div>
            <div className="fair-play-case-img">
              <img
                src={getOdd?.getCase?.getImage}
                alt={t(`odds_alt`, {
                  caseName: getOdd?.getCase?.getName,
                })}
              />
            </div>
            <div className="fair-play-case-peroid">
              <div className="date">
                <div className="date-time">
                  {dayjs(getOdd?.createdAt).format('YYYY-MM-DD hh:mm:ss')}
                </div>

                <div className="date-now">
                  {getOdd?.status
                    ? dayjs(getOdd?.updatedAt).format('YYYY-MM-DD hh:mm:ss')
                    : t('NOW')}
                </div>
              </div>
              <div className="fair-play-case-peroid-head">
                <div className="title">{t('ID period')}:</div>
              </div>
              <div className="fair-play-case-peroid-input" onClick={onClick}>
                <span className="icon material-icons" data-icon="&#xe8be;"></span>
                <input type="text" defaultValue={getOdd.hashId} readOnly />
                <div className="icon material-icons copy" data-icon="content_copy">
                  {isCopy && <span>{t('Copied')}</span>}
                </div>
              </div>
              <div className="search-block">
                <input
                  type="text"
                  placeholder={t('ID period')}
                  value={value}
                  onChange={onChange}
                />
                <a className="btn-search" href="#" onClick={onSearch}>
                  <span className="material-icons">search</span>
                </a>
              </div>
            </div>
          </div>
          <div className="in-case">
            <div className="in-case-title">
              <span>{t('Case Contains')}</span>
            </div>
          </div>
          <div className="fair-play-case-table">
            <div className="fair-play-case-table-head">
              <div className="fair-play-case-table-head-td">{t('Item')}</div>
              <div className="fair-play-case-table-head-td">{t('Price')}</div>
              <div className="fair-play-case-table-head-td">{t('Range')}</div>
              <div className="fair-play-case-table-head-td">
                <span>{t('Price')}</span>
                <span>{t('Range')}</span>
                <span>{t('Chance')}</span>
              </div>
            </div>
            <div className="fair-play-case-table-body">
              {_.map(getOdd?.getItems, getItem => (
                <Item
                  getItem={getItem}
                  key={getItem.id}
                  itemsOdds={getOdd?.itemsOdds}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const OddsWarp = () => {
  const { loading, data, error } = useGetCase({});

  const getCase = data?.getCase;

  if (loading) {
    return <Loader />;
  } else if (error) {
    return <>Odds | Error! {error.message}</>;
  }

  if (!getCase?.getOdd?.hashId) {
    return <NotFound />;
  }

  return <Odds defaultHashId={getCase?.getOdd?.hashId} />;
};

export default OddsWarp;
